import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../auth/useAuth';
import { useChatSessions } from '../../../features/chat/chatSessions/useChatSessions';
import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import { useUserConfig } from '../../../userConfig/useUserConfig';
import type {
  ChatMember,
  InvitableCompanyUser,
} from '../../../types/chatMember';
import { LiksButton } from '../LiksButton/LiksButton';
import { LiksDialog } from '../LiksDialog/LiksDialog';
import { LiksSearch } from '../LiksSearch/LiksSearch';
import { ChatSessionShareInviteRow } from './ChatSessionShareInviteRow';
import { ChatSessionShareMemberRow } from './ChatSessionShareMemberRow';

export type ChatSessionShareDialogContentProps = {
  sessionId: string;
  isShareMode: boolean;
  sessionTitle: string;
  onLeave: (sessionId: string, sessionTitle: string) => void;
  onClose: () => void;
};

export function ChatSessionShareDialogContent({
  sessionId,
  isShareMode,
  sessionTitle,
  onLeave,
  onClose,
}: ChatSessionShareDialogContentProps) {
  const { user } = useAuth();
  const { translate } = useI18n();
  const { markPlatformChatSessionSeen } = useUserConfig();
  const {
    getSessionById,
    listChatMembers,
    listInvitableCompanyUsers,
    inviteChatParticipant,
    uninviteChatParticipant,
    transferOwnership,
  } = useChatSessions();
  const [searchQuery, setSearchQuery] = useState('');
  const [members, setMembers] = useState<ChatMember[]>([]);
  const [invitableUsers, setInvitableUsers] = useState<InvitableCompanyUser[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);

  const loadMembersAndInvitable = async (
    query: string,
    nextMembers?: ChatMember[],
  ) => {
    const membersList = nextMembers ?? (await listChatMembers(sessionId));
    const invitable = await listInvitableCompanyUsers(sessionId, query);
    setMembers(membersList);
    setInvitableUsers(invitable);
  };

  const isOwner = useMemo(
    () =>
      members.some(
        (member) => member.userId === user?.id && member.chatRole === 'owner',
      ),
    [members, user?.id],
  );

  useEffect(() => {
    void (async () => {
      setIsLoading(true);
      await loadMembersAndInvitable(searchQuery);
      setIsLoading(false);
    })();
    // Runs on open; search-based updates are handled by onChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    void (async () => {
      const invitable = await listInvitableCompanyUsers(sessionId, query);
      setInvitableUsers(invitable);
    })();
  };

  const handleInvite = async (userId: string) => {
    const invitee =
      invitableUsers.find((userItem) => userItem.userId === userId) ??
      members.find((member) => member.userId === userId);
    const name = invitee?.name ?? userId;

    try {
      await inviteChatParticipant(sessionId, userId);
      toast.success({
        title: translate('chatSessions.inviteSuccessTitle'),
        description: translate('chatSessions.inviteSuccessDescription', {
          name,
        }),
      });
      await loadMembersAndInvitable(searchQuery);
      const sharedSession = getSessionById(sessionId);
      if (sharedSession?.mostRecentMessageId) {
        markPlatformChatSessionSeen(
          sessionId,
          sharedSession.mostRecentMessageId,
          { persist: true },
        );
      }
    } catch (error) {
      await toastMutationError(
        error,
        translate('chatSessions.shareInviteLabel'),
        translate('chatSessions.pinErrorDescription'),
      );
    }
  };

  const handleUninvite = async (userId: string) => {
    const member = members.find((memberItem) => memberItem.userId === userId);
    const name = member?.name ?? userId;

    try {
      await uninviteChatParticipant(sessionId, userId);
      toast.success({
        title: translate('chatSessions.removeSuccessTitle'),
        description: translate('chatSessions.removeSuccessDescription', {
          name,
        }),
      });
      await loadMembersAndInvitable(searchQuery);
    } catch (error) {
      await toastMutationError(
        error,
        translate('chatSessions.shareRemoveLabel'),
        translate('chatSessions.pinErrorDescription'),
      );
    }
  };

  const handleTransferOwnership = async (newOwnerUserId: string) => {
    await transferOwnership(sessionId, newOwnerUserId);
    await loadMembersAndInvitable(searchQuery);
  };

  const handleLeave = () => {
    onLeave(sessionId, sessionTitle);
  };

  return (
    <LiksDialog
      isOpen
      size="xl"
      className="liks-chat-share"
      bodyClassName="liks-chat-share__body"
      title={
        isShareMode
          ? translate('chatSessions.shareTitle')
          : translate('chatSessions.participants')
      }
      closeLabel={translate('chatSessions.shareClose')}
      onClose={onClose}
      footer={
        <LiksButton variant="secondary" onClick={onClose}>
          {translate('chatSessions.shareClose')}
        </LiksButton>
      }
    >
      <div className="liks-chat-share__section">
        <h3 className="liks-chat-share__section-title">
          {translate('chatSessions.shareMembersTitle')}
        </h3>
        <ul className="liks-chat-share__list">
          {isLoading && (
            <li className="liks-chat-share__empty">
              {translate('chatSessions.loadingMembers')}
            </li>
          )}
          {members.map((member) => {
            const isCurrentUser = member.userId === user?.id;

            return (
              <ChatSessionShareMemberRow
                key={member.userId}
                member={member}
                isCurrentUser={isCurrentUser}
                isOwner={isOwner}
                sessionTitle={sessionTitle}
                translate={translate}
                onTransferOwnership={handleTransferOwnership}
                onUninvite={handleUninvite}
                onLeave={handleLeave}
              />
            );
          })}
        </ul>
      </div>

      {isOwner && (
        <div className="liks-chat-share__section">
          <h3 className="liks-chat-share__section-title">
            {translate('chatSessions.shareInviteTitle')}
          </h3>
          <LiksSearch
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={translate('chatSessions.shareSearchPlaceholder')}
            aria-label={translate('chatSessions.shareSearchLabel')}
            clearLabel={translate('common.search.clear')}
            variant="on-light"
          />
          <ul className="liks-chat-share__list">
            {invitableUsers.length === 0 ? (
              <li className="liks-chat-share__empty">
                {searchQuery.trim() === ''
                  ? translate('chatSessions.shareInviteEmpty')
                  : translate('chatSessions.shareInviteNoResults')}
              </li>
            ) : (
              invitableUsers.map((invitee) => (
                <ChatSessionShareInviteRow
                  key={invitee.userId}
                  invitee={invitee}
                  translate={translate}
                  onInvite={handleInvite}
                />
              ))
            )}
          </ul>
        </div>
      )}
    </LiksDialog>
  );
}
